/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  EventType,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig = {
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['openid', 'profile'],
};

export let msalInstance = null;

export const setInstance = (value) => {
  msalInstance = value;
};

export let mockMode = false;

export const setMockMode = (value) => {
  mockMode = value;
};

/*
 * Azure issues ID tokens with a lifetime of about an hour. `acquireTokenSilent` decides whether to
 * renew from the *access* token it holds, so while only OIDC scopes are requested it keeps handing
 * back the cached ID token long after that token's own `exp` has passed. The backend validates
 * `exp` (AzureAuthenticator::authenticate -> validateAccessToken) and answers 403 "Expired token",
 * which is what breaks a long configurator session mid-click. Checking the expiry here is what
 * prevents it.
 */
const TOKEN_EXPIRY_SKEW_SECONDS = 300;

const readTokenExpiry = (token) => {
  try {
    const payload = String(token).split('.')[1];
    if (!payload) return null;
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' ? exp : null;
  } catch (e) {
    return null;
  }
};

// A token whose expiry cannot be read counts as expiring. Renewing costs one silent call; sending
// it costs a rejected request and a broken screen.
export const isTokenExpiring = (token, skewSeconds = TOKEN_EXPIRY_SKEW_SECONDS) => {
  const exp = readTokenExpiry(token);
  return exp === null || exp - skewSeconds <= Date.now() / 1000;
};

export const acquireIdToken = async (forceRefresh = false) => {
  if (mockMode) return 'mock-token';

  const activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
  const accounts = msalInstance.getAllAccounts();

  if (!activeAccount && accounts.length === 0) {
    return null;
  }
  const request = {
    scopes: loginRequest.scopes,
    account: activeAccount || accounts[0],
  };

  try {
    const authResult = await msalInstance.acquireTokenSilent({ ...request, forceRefresh });
    if (forceRefresh || !isTokenExpiring(authResult.idToken)) {
      return authResult.idToken;
    }

    const renewed = await msalInstance.acquireTokenSilent({ ...request, forceRefresh: true });
    return renewed.idToken;
  } catch (error) {
    // The refresh token is gone or Azure wants the user to interact again. Nothing silent recovers
    // from that, so hand over to the redirect flow instead of sending a token we know is dead.
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(request);
    }
    throw error;
  }
};
